<template>
  <div>
    <DxPopup
      :visible="showReportPopup"
      :drag-enabled="false"
      :close-on-outside-click="false"
      :show-close-button="true"
      :show-title="true"
      height="auto"
      width="700"
      container=".dx-viewport"
      :title="$t('alert.sampleSummary')"
      @hiding="hide"
    >
      <div v-if="!loaded" class="alert-preloader-overlay">
        <div class="alert-preloader-card">
          <f7-preloader></f7-preloader>
          <div class="alert-preloader-text">
            {{ $t('loading') }}
          </div>
        </div>
      </div>
      <div v-else style="padding: 15px">
        <f7-row>
          <f7-col>
            <f7-row>
              <f7-col>
                <div style="padding: 5px">
                  <strong>{{ $t('company') }}:</strong>
                  {{ companyName }}
                </div>
              </f7-col>
            </f7-row>
          </f7-col>
          <f7-col>
            <f7-row>
              <f7-col>
                <div style="padding: 5px">
                  <strong>{{ $t('address') }}:</strong>
                  {{ addressName }}
                </div>
              </f7-col>
            </f7-row>
          </f7-col>
        </f7-row>

        <f7-row>
          <f7-col>
            <f7-row>
              <f7-col>
                <div style="padding: 5px">
                  <strong>
                    {{ $t('agronomySample.summary.sampleCode') }}:
                  </strong>
                  {{ details.sample_code }}
                </div>
              </f7-col>
            </f7-row>
          </f7-col>
        </f7-row>

        <f7-row>
          <f7-col>
            <f7-row>
              <f7-col>
                <div style="padding: 5px">
                  <strong>{{ $t('atc') }}:</strong>
                  {{ details.atc }}
                </div>
              </f7-col>
            </f7-row>
          </f7-col>

          <f7-col>
            <f7-row>
              <f7-col>
                <div style="padding: 5px">
                  <strong>
                    {{ $t('agronomySample.summary.sampleType') }}:
                  </strong>
                  {{ details.sample_type }}
                </div>
              </f7-col>
            </f7-row>
          </f7-col>
        </f7-row>

        <f7-row>
          <f7-col>
            <f7-row>
              <f7-col>
                <div style="padding: 5px">
                  <strong>
                    {{ $t('environmentSamples.samplingPoint') }}:
                  </strong>
                  {{ details.parcel }}
                </div>
              </f7-col>
            </f7-row>
          </f7-col>
          <f7-col>
            <f7-row>
              <f7-col>
                <div style="padding: 5px">
                  <strong>
                    {{ $t('foodSamples.samplingSite') }}:
                  </strong>
                  {{ details.farm }}
                </div>
              </f7-col>
            </f7-row>
          </f7-col>
        </f7-row>

        <f7-row>
          <f7-col>
            <f7-row>
              <f7-col>
                <div style="padding: 5px">
                  <strong>
                    {{ $t('agronomySample.summary.description') }}:
                  </strong>
                  {{ details.sample_name || '' }}
                </div>
              </f7-col>
            </f7-row>
          </f7-col>
        </f7-row>

        <f7-row>
          <f7-col>
            <f7-row>
              <f7-col>
                <div style="padding: 5px">
                  <strong> {{ $t('project') }}: </strong>
                  {{ details.study || '' }}
                </div>
              </f7-col>
            </f7-row>
          </f7-col>
        </f7-row>

        <f7-row>
          <SampleDetailsTable
            class="marginTop15"
            :details="details"
          />
        </f7-row>
      </div>
    </DxPopup>
  </div>
</template>

<script>
import { DxPopup } from 'devextreme-vue/popup';
import { mapActions, mapState } from 'vuex';

import DxButton from 'devextreme-vue/button';
import { resolveAtcKey } from '@/utils/atcRouting';
import SampleDetailsTable from './Table/index.vue';

export default {
  components: {
    DxPopup,
    SampleDetailsTable,
    DxButton,
  },
  props: {
    details: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      loaded: true,
    };
  },
  computed: {
    ...mapState('alertsSample', ['showReportPopup']),
    ...mapState('contact', ['companyName', 'addressName']),
  },
  methods: {
    hide() {
      this.setShowReportPopup(false);
    },
    goToAlertEdit() {
      this.setShowReportPopup(false);
      this.setFocusedRowKey(this.details.sample_alert);

      this.$f7router.navigate(
        {
          name: 'AlertsSamplePage',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    async goToSampleDetailsWithFilters() {
      this.loaded = false;
      try {
        this.setReportSampleCode(this.details.sample_code);
        const currentSample = await this.getCurrentSampleByCode();
        const currentSampleId =
          currentSample?.id ||
          // eslint-disable-next-line no-underscore-dangle
          currentSample?._id ||
          currentSample?.sample_id;

        if (!currentSampleId) return;

        this.setCurrentSampleId(currentSampleId);
        this.setShowReportPopup(false);

        await this.goToSampleDetailsByAtc(
          currentSample?.atc_name,
          currentSample
        );
      } finally {
        this.loaded = true;
      }
    },
    async goToSampleDetailsByAtc(atcName) {
      const atcKey = resolveAtcKey({
        atc_name: atcName,
        atc_code: this.details?.atc_code,
      });

      if (atcKey === 'FOOD') {
        this.setCurrentFilter('foodDetails');
        this.setFoodDetailsSelectedParameters([]);

        if (this.details.parameters[0].cma_value !== null) {
          this.setFoodDetailsSelectedLimits(['> CMA']);
        } else {
          this.setFoodDetailsSelectedLimits(['> LMR']);
        }

        this.$f7router.navigate(
          {
            name: 'FoodSampleDetailPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );

        this.applyCurrentFilters();
      } else if (atcKey === 'AGRONOMY') {
        this.setCurrentFilter('agronomy');

        this.$f7router.navigate(
          {
            name: 'AgronomySamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcKey === 'ENVIRONMENT') {
        this.setCurrentFilter('environmentDetails');

        this.setEnvironmentDetailsSelectedParameters([]);

        this.setEnvironmentSelectedLimits(['> CMA']);

        this.$f7router.navigate(
          {
            name: 'EnvironmentSamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );

        this.applyCurrentFilters();
      } else if (atcKey === 'MINING') {
        this.setCurrentFilter('miningDetails');

        this.setMiningDetailsSelectedParameters([]);

        this.$f7router.navigate(
          {
            name: 'MiningSamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );

        this.applyCurrentFilters();
      } else if (atcKey === 'HEALTH_SECURITY') {
        this.setCurrentFilter('healthSecurityDetails');

        this.setHealthSecurityDetailsSelectedParameters([]);
        this.setHealthSecuritySelectedLimits(['> CMA']);

        this.$f7router.navigate(
          {
            name: 'HealthSecuritySamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );

        this.applyCurrentFilters();
      }
    },
    ...mapActions('alertsSample', [
      'setShowReportPopup',
      'setFocusedRowKey',
    ]),
    ...mapActions('filter', [
      'setCurrentFilter',
      'setCurrentSampleId',
    ]),
    ...mapActions('samples', [
      'getCurrentSampleByCode',
      'setReportSampleCode',
    ]),
    ...mapActions('foodDetailsFilter', {
      setFoodDetailsSelectedLimits: 'setSelectedLimits',
      setFoodDetailsSelectedParameters: 'setSelectedParameters',
    }),
    ...mapActions('environmentDetailsFilter', {
      setEnvironmentDetailsSelectedParameters:
        'setSelectedParameters',
      setEnvironmentSelectedLimits: 'setSelectedLimits',
    }),
    ...mapActions('miningDetailsFilter', {
      setMiningDetailsSelectedParameters: 'setSelectedParameters',
    }),
    ...mapActions('healthSecurityDetailsFilter', {
      setHealthSecuritySelectedLimits: 'setSelectedLimits',
      setHealthSecurityDetailsSelectedParameters:
        'setSelectedParameters',
    }),
    ...mapActions('filter', ['applyCurrentFilters']),
  },
};
</script>
<style scoped>
.alert-preloader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background: rgba(255, 255, 255, 0.15);
}

.alert-preloader-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px 36px;
  min-width: 140px;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.alert-preloader-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--Primary-Blue-60, #3e3c44);
}
</style>
